import { useState } from "react";
import { ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PERM_GROUPS, ROLE_PERMS, can } from "@/lib/perms";
import { BigButton } from "@/components/ui-kit";

export default function PermissionsDialog({ member, onClose }) {
  const { t, syncNow } = useApp();
  const [denied, setDenied] = useState(new Set(member.denied || []));
  const [busy, setBusy] = useState(false);
  const toggle = (k, allowed) => setDenied((d) => { const n = new Set(d); if (allowed) n.delete(k); else n.add(k); return n; });
  const groups = PERM_GROUPS.filter((g) => can(member.role, g.perm));
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/staff/${member.id}/permissions`, { denied: [...denied] }); toast.success(t("saved")); syncNow(true); onClose(); } catch { toast.error(t("error")); } finally { setBusy(false); }
  };
  const allOff = (g, off) => setDenied((d) => { const n = new Set(d); [`page:${g.perm}`, ...g.ops].forEach((k) => (off ? n.add(k) : n.delete(k))); return n; });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="permissions-dialog">
        <DialogTitle className="font-heading text-xl flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#1F4E8C]" />{t("perm_title")} · {member.name} <span className="text-sm font-normal text-muted-foreground">({t(`role_${member.role}`)})</span></DialogTitle>
        <p className="text-xs text-muted-foreground">{t("perm_hint")}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {groups.map((g) => {
            const pageOn = !denied.has(`page:${g.perm}`);
            return (
              <div key={g.perm} className={`rounded-lg border p-3 ${pageOn ? "border-[#CCCCCC] bg-white" : "border-[#E74C3C]/40 bg-[#FDF2F2]"}`} data-testid={`perm-group-${g.perm}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <label className="font-bold text-sm flex items-center gap-2"><Switch checked={pageOn} onCheckedChange={(v) => toggle(`page:${g.perm}`, v)} data-testid={`perm-page-${g.perm}`} />{t(`perm_${g.perm}`)}</label>
                  <div className="flex gap-1 text-[11px]"><button type="button" onClick={() => allOff(g, false)} className="px-2 h-6 rounded border border-border bg-white" data-testid={`perm-all-on-${g.perm}`}>{t("perm_all_on")}</button><button type="button" onClick={() => allOff(g, true)} className="px-2 h-6 rounded border border-border bg-white" data-testid={`perm-all-off-${g.perm}`}>{t("perm_all_off")}</button></div>
                </div>
                <div className="grid gap-1.5">
                  {g.ops.map((op) => (
                    <label key={op} className="flex items-center justify-between gap-2 text-sm rounded px-2 py-1 hover:bg-[#F2F6FB]">
                      <span className={denied.has(op) ? "line-through text-muted-foreground" : ""}>{t(`act_${op}`)}</span>
                      <Switch checked={!denied.has(op)} onCheckedChange={(v) => toggle(op, v)} data-testid={`perm-op-${op}`} />
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <BigButton variant="soft" onClick={onClose} data-testid="perm-cancel">{t("back")}</BigButton>
          <BigButton onClick={save} disabled={busy} data-testid="perm-save"><Save className="w-5 h-5" />{t("save")} ({denied.size} {t("perm_blocked")})</BigButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
